import { useState, useRef, useCallback, useEffect } from 'react'
import { pwaApi, Employee, ConversationItem, ScriptStreamStep, ScriptStreamMeta, GuardStatus, AnalyzeNoteResponse } from '../api'
import type { Arrangement } from '../types/arrangement'
import type { IncidentForm } from '../types/incident'
import { formatIncidentTitle } from '../utils/incidentParser'
import { loadBaseDictionaries } from '../engines/shared/dictionary_service'
import { getNotes } from '../engines/offline_storage'
import type { NoteRecord } from '../engines/types'

export interface ConversationState {
  conversationId: string | number | null
  selectedEmployee: string
  tailoredScript: null
  scriptLoading: boolean
  scriptBriefing: string | null
  scriptModel: string | null  // E2: model rozmowy wybrany przez LLM (np. 'seven_steps')
  scriptSteps: ScriptStreamStep[]
  scriptStreamDone: boolean
  arrangementsText: string
  decisionContent: string
  apiEmployees: Employee[] | null
  conversationItems: ConversationItem[] | null
  isProcessing: boolean
  transcriptionText: string
  noteUpdate: { name: string; note: string } | null
  guardStatus: GuardStatus | null
  complianceWarning: string | null
  isIncident: boolean
  incidentForm: IncidentForm | null
  originalIncidentForm: IncidentForm | null
  lastNoteId: string | null
}

export interface ConversationActions {
  setSelectedEmployee: (name: string) => void
  setArrangementsText: (text: string) => void
  setDecisionContent: (text: string) => void
  setTranscriptionText: (text: string) => void
  setNoteUpdate: (val: { name: string; note: string } | null) => void
  setIsIncident: (isIncident: boolean) => void
  setIncidentForm: (form: IncidentForm | null) => void
  setOriginalIncidentForm: (form: IncidentForm | null) => void
  setGuardStatus: (status: GuardStatus | null) => void
  setComplianceWarning: (warning: string | null) => void
  setScriptBriefing: (briefing: string | null) => void
  setLastNoteId: (id: string | null) => void
  loadEmployees: () => void
  loadConversationItems: () => void
  setApiEmployees: React.Dispatch<React.SetStateAction<Employee[] | null>>
  setConversationItems: React.Dispatch<React.SetStateAction<ConversationItem[] | null>>
  abortScriptStream: () => void

  // Ekran #1: po zakończeniu dyktowania — zapis do DB + przejście na ekran 2
  runDictationPipeline: (fullText: string, employeeName: string, onNavigate: () => void, analyzePromise?: Promise<AnalyzeNoteResponse>, forceNewEmployee?: boolean, onGuardBlock?: () => void) => void

  // Ekran #3: finalizacja rozmowy (tekst lub audio)
  finalizeAndSend: (params: {
    actionType: 'NOTE' | 'WARNING' | 'REPRIMAND'
    arrangements?: string
    arrangementList?: Arrangement[]
    existingNote?: string
    optimisticId?: string
    onClipboard?: (text: string) => void
    onDone?: () => void
  }) => void

  // Reset całego stanu po powrocie do ekranu 1
  resetConversation: () => void
  resetConversationId: () => void
}

export function useConversation(): ConversationState & ConversationActions {
  const [conversationId, setConversationId]     = useState<string | number | null>(null)
  const [selectedEmployee, setSelectedEmployee]  = useState<string>('')
  const [tailoredScript, setTailoredScript]      = useState<null>(null)
  const [scriptLoading, setScriptLoading]        = useState(false)
  const [scriptBriefing, setScriptBriefing]      = useState<string | null>(null)
  const [scriptModel, setScriptModel]            = useState<string | null>(null)  // E2: model rozmowy
  const [scriptSteps, setScriptSteps]            = useState<ScriptStreamStep[]>([])
  const [scriptStreamDone, setScriptStreamDone]  = useState(false)
  const [arrangementsText, setArrangementsText]  = useState<string>('')
  const [decisionContent, setDecisionContent]    = useState<string>('')
  const [apiEmployees, setApiEmployees]          = useState<Employee[] | null>(null)
  const [conversationItems, setConversationItems] = useState<ConversationItem[] | null>(null)
  const [isProcessing, setIsProcessing]          = useState(false)
  const [transcriptionText, setTranscriptionText] = useState<string>('')
  const [noteUpdate, setNoteUpdate]              = useState<{ name: string; note: string } | null>(null)
  const [guardStatus, setGuardStatus]            = useState<GuardStatus | null>(null)
  const [complianceWarning, setComplianceWarning] = useState<string | null>(null)
  const [isIncident, setIsIncident]              = useState(false)
  const [incidentForm, setIncidentFormState]     = useState<IncidentForm | null>(null)
  const [originalIncidentForm, setOriginalIncidentForm] = useState<IncidentForm | null>(null)
  const [lastNoteId, setLastNoteId]              = useState<string | null>(null)

  const scriptAbortRef = useRef<AbortController | null>(null)

  const loadEmployees = useCallback(() => {
    pwaApi.getEmployees()
      .then(res => setApiEmployees(res.employees))
      .catch(() => setApiEmployees(null))
  }, [])

  const buildIncidentHistoryItems = (): ConversationItem[] => {
    const historyRaw = JSON.parse(localStorage.getItem('incidentHistory') || '[]') as any[]
    const seen = new Set<string>()
    const history = historyRaw.filter((h: any) => {
      const key = [h.lokalizacja, h.obiekt, h.typ, h.waznosc, h.opis, h.createdAt ? h.createdAt.slice(0, 19) : ''].join('|')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    return history.map((h: any) => ({
      conversation_id: h.id,
      employee_id: 0,
      first_name: formatIncidentTitle(h).bold,
      last_name: formatIncidentTitle(h).rest,
      position: null,
      short_summary: h.lokalizacja || h.opis || '',
      long_summary: h.opis || '',
      arrangements_transcription: '',
      arrangements_json: h.polecenia_arr || [],
      conversation_status: 'ACCEPTABLE',
      created_at: h.createdAt,
      is_indexing: false,
      _incident: true,
      incident_form: h as import('../types/incident').IncidentForm,
    }))
  }

  // Notatki offline (IndexedDB) jeszcze niezsynchronizowane z backendem — pokazujemy je
  // od razu na liście E1, żeby nie znikały do czasu synchronizacji.
  const buildOfflineNoteItems = async (): Promise<ConversationItem[]> => {
    const notes = await getNotes().catch(() => [] as NoteRecord[])
    return notes
      .filter(n => {
        if (n.guardStatus && n.guardStatus !== 'OK') return false
        // Incydenty (ścieżka B) offline nie mają dziś żadnego trwałego odpowiednika
        // w backendzie (offline-sync tylko wysyła e-mail/PDF, nie tworzy rekordu
        // w bazie) — więc IndexedDB jest dla nich JEDYNYM źródłem prawdy i musi
        // pozostać widoczne na liście również PO synchronizacji, inaczej incydent
        // zniknąłby z E1 zamiast zostać tam raz na zawsze. Rozmowy (ścieżka A) po
        // synchronizacji mają już swój rekord w backendzie, więc te chowamy.
        if (n.path === 'B') return true
        return !n.synced
      })
      .map((n: NoteRecord): ConversationItem => {
        if (n.path === 'B' && n.incidentForm) {
          const { bold, rest } = formatIncidentTitle(n.incidentForm)
          return {
            conversation_id: n.id as any,
            employee_id: 0,
            first_name: bold,
            last_name: rest,
            position: null,
            short_summary: n.shortNote || '',
            long_summary: n.script || n.shortNote || '',
            arrangements_transcription: '',
            arrangements_json: n.incidentForm.polecenia_arr || [],
            conversation_status: 'ACCEPTABLE',
            created_at: new Date(n.createdAt).toISOString(),
            is_indexing: false,
            _incident: true,
            incident_form: n.incidentForm,
          }
        }
        return {
          conversation_id: n.id as any,
          employee_id: 0,
          first_name: n.title || n.entities.employee || 'Pracownik X',
          last_name: '',
          position: null,
          short_summary: n.shortNote || '',
          long_summary: n.script || n.shortNote || '',
          arrangements_transcription: '',
          arrangements_json: [],
          conversation_status: 'ACCEPTABLE',
          created_at: new Date(n.createdAt).toISOString(),
          is_indexing: false,
        }
      })
  }

  const loadConversationItems = useCallback(() => {
    Promise.allSettled([pwaApi.getConversations(), buildOfflineNoteItems()])
      .then(([convRes, offlineRes]) => {
        const backendItems = convRes.status === 'fulfilled' ? convRes.value.items : []
        const offlineItems = offlineRes.status === 'fulfilled' ? offlineRes.value : []
        const incidentItems = buildIncidentHistoryItems()

        const mergedMap = new Map<string | number, ConversationItem>()
        for (const item of [...backendItems, ...incidentItems, ...offlineItems]) { mergedMap.set(item.conversation_id, item) }
        const merged = [...mergedMap.values()].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        setConversationItems(merged)
      })
  }, [])

  const indexingPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Polling tylko dla PRAWDZIWYCH is_indexing z backendu — NIE dla optimistic itemów
    const hasIndexing = conversationItems?.some(c => c.is_indexing && !c._optimistic) ?? false
    if (hasIndexing && !indexingPollRef.current) {
      indexingPollRef.current = setInterval(() => {
        pwaApi.getConversations()
          .then(res => {
            setConversationItems(prev => {
              // Zachowaj optimistic itemy — polling ich nie nadpisuje
              const optimisticItems = (prev ?? []).filter(item => item._optimistic)
              const optimisticConvIds = new Set(optimisticItems.map(i => i.conversation_id))
              const backendFiltered = res.items.filter((i: import('../api').ConversationItem) => !optimisticConvIds.has(i.conversation_id))
              return [...optimisticItems, ...backendFiltered]
            })
            if (!res.items.some((c: import('../api').ConversationItem) => c.is_indexing)) {
              clearInterval(indexingPollRef.current!)
              indexingPollRef.current = null
            }
          })
          .catch(() => {})
      }, 4000)
    } else if (!hasIndexing && indexingPollRef.current) {
      clearInterval(indexingPollRef.current)
      indexingPollRef.current = null
    }
    return () => {}
  }, [conversationItems])

  // Refy do odczytu aktualnego stanu bez setState-wewnątrz-setState anty-patternu
  const apiEmployeesRef      = useRef<Employee[] | null>(null)
  const conversationItemsRef = useRef<ConversationItem[] | null>(null)
  apiEmployeesRef.current      = apiEmployees
  conversationItemsRef.current = conversationItems

  const abortScriptStream = useCallback(() => {
    if (scriptAbortRef.current) {
      scriptAbortRef.current.abort()
      scriptAbortRef.current = null
    }
  }, [])

  const startScriptStream = useCallback((convId: number, onReady?: () => void) => {
    abortScriptStream()
    setScriptBriefing(null)
    setScriptSteps([])
    setScriptStreamDone(false)
    setGuardStatus(null)
    setComplianceWarning(null)
    setScriptLoading(true)

    let readyCalled = false
    const callReady = () => {
      if (!readyCalled) { readyCalled = true; onReady?.() }
    }

    const ac = pwaApi.selectScriptStream(convId, {
      onMeta: (meta: ScriptStreamMeta) => {
        setScriptBriefing(meta.briefing)
        setScriptModel(meta.model)  // E2: zapisz model rozmowy
        if (meta.complianceWarning) setComplianceWarning(meta.complianceWarning)
        callReady()
      },
      onStep: (step: ScriptStreamStep) => {
        setScriptSteps(prev => [...prev, step])
      },
      onDone: () => {
        setScriptStreamDone(true)
        setScriptLoading(false)
        callReady() // safety-net: jeśli onReady jeszcze nie wywołane
      },
      onError: (msg: string) => {
        console.error('[stream] script error:', msg)
        setScriptStreamDone(true)
        setScriptLoading(false)
        callReady() // safety-net
      },
      onGuard: (status: GuardStatus) => {
        setGuardStatus(status)
        setScriptStreamDone(true)
        setScriptLoading(false)
        callReady()
      },
    })
    scriptAbortRef.current = ac
  }, [abortScriptStream])

  const runDictationPipeline = useCallback((
    fullText: string,
    employeeName: string,
    onDone: () => void,
    analyzePromise?: Promise<AnalyzeNoteResponse>,
    forceNewEmployee?: boolean,  // true = heurystyka (zawsze nowy pracownik)
    onGuardBlock?: () => void    // wywoływany gdy NOT_BUSINESS lub MOBBING_WARNING
  ) => {
    console.log('[DEBUG] runDictationPipeline START', { fullText: fullText.slice(0,30), employeeName, hasAnalyzePromise: !!analyzePromise, forceNewEmployee })
    // analyzePromise rozwiązuje imię i cleanText max 8s
    // NAJPIERW czekamy na wynik — jeśli NOT_BUSINESS, przerywamy pipeline bez tworzenia rozmowy
    const analyzeWithTimeout: Promise<AnalyzeNoteResponse> = analyzePromise
      ? Promise.race([
          analyzePromise.catch(() => ({ detectedName: employeeName, cleanText: fullText, status: 'OK', warningText: '', reason: '' })),
          new Promise<AnalyzeNoteResponse>(resolve =>
            setTimeout(() => resolve({ detectedName: employeeName, cleanText: fullText, status: 'OK', warningText: '', reason: '' }), 8000)
          ),
        ])
      : Promise.resolve({ detectedName: employeeName, cleanText: fullText, status: 'OK', warningText: '', reason: '' })

    const emps = apiEmployeesRef.current

    const launchStream = (employeeId: number, resolvedName: string, cleanText: string) => {
      // FIX #1: Synchronizacja - najpierw stream, potem saveNote
      // Gwarantuje że sesja istnieje przez cały czas generowania odprawy
      console.log('[DEBUG] launchStream start, employeeId:', employeeId)
      pwaApi.createConversation(employeeId, fullText)
        .then(res => {
          console.log('[DEBUG] createConversation OK, id:', res.conversationId)
          // FIX: Update conversationId immediately after createConversation
          // This ensures E3/E1 have the correct real ID instead of tmp ID
          setConversationId(res.conversationId)
          const noteText = (resolvedName && resolvedName !== 'PracownikX')
            ? `${resolvedName}: ${cleanText}`
            : cleanText
          
          // SSE stream z podzielonymi callbackami:
          // - onMeta: nawigacja na E2 (od razu)
          // - onDone: zapis notatki (po zakończeniu streamu)
          console.log('[DEBUG] Starting SSE stream...')
          setScriptLoading(true)
          let navTriggered = false
          const ac = pwaApi.selectScriptStream(res.conversationId, {
            onMeta: (meta) => {
              console.log('[DEBUG] SSE onMeta received:', meta)
              setScriptBriefing(meta.briefing)
              setScriptModel(meta.model)
              if (meta.complianceWarning) setComplianceWarning(meta.complianceWarning)
              // Nawigacja natychmiast po otrzymaniu metadanych
              if (!navTriggered) { navTriggered = true; onDone() }
            },
            onStep: (step) => { 
              console.log('[DEBUG] SSE onStep:', step.step)
              setScriptSteps(prev => [...prev, step]) 
            },
            onDone: () => {
              console.log('[DEBUG] SSE onDone')
              setScriptStreamDone(true)
              setScriptLoading(false)
              // Zapis notatki dopiero PO zakończeniu streamu (fix race condition)
              pwaApi.saveNote(res.conversationId, noteText)
                .then((saveRes) => {
                  // FIX: Użyj real conversationId zwróconego przez saveNote
                  if (saveRes.conversationId) {
                    console.log('[DEBUG] Updating conversationId to real ID:', saveRes.conversationId)
                    setConversationId(saveRes.conversationId)
                  }
                  return pwaApi.getConversations()
                })
                .then(r => { setConversationItems(r.items) })
                .catch(err => console.error('[pipeline] saveNote error:', err))
            },
            onError: (msg) => {
              console.error('[DEBUG] SSE onError:', msg)
              setScriptStreamDone(true)
              setScriptLoading(false)
              // Nawigacja nawet przy błędzie, ale bez saveNote
              if (!navTriggered) { navTriggered = true; onDone() }
            },
            onGuard: (status) => {
              console.log('[DEBUG] SSE onGuard:', status)
              setGuardStatus(status)
              setScriptStreamDone(true)
              setScriptLoading(false)
              if (!navTriggered) { navTriggered = true; onDone() }
            }
          })
          scriptAbortRef.current = ac
        })
        .catch(err => { console.error('[pipeline] launchStream error:', err); onDone() })
    }

    analyzeWithTimeout.then(async analyzeResult => {
      console.log('[DEBUG] analyzeResult:', analyzeResult)
      // Blokuj pipeline gdy NOT_BUSINESS, MOBBING_WARNING lub PRIVACY_WARNING — nie tworzymy rozmowy, nie nawigujemy
      // ALE zapisujemy odrzuconą notatkę do bazy dla audytu
      if (['NOT_BUSINESS', 'MOBBING_WARNING', 'PRIVACY_WARNING'].includes(analyzeResult.status)) {
        console.log('[DEBUG] BLOCKED by guard:', analyzeResult.status)
        setGuardStatus(analyzeResult.status as GuardStatus)
        setScriptStreamDone(true)
        setScriptLoading(false)
        
        // ZAPISZ odrzuconą notatkę do bazy (tekst ORYGINALNY, niepoprawiony)
        try {
          const resolvedName = (analyzeResult.detectedName && analyzeResult.detectedName !== 'PracownikX')
            ? analyzeResult.detectedName
            : employeeName
          
          let employeeId: number | null = null
          
          // Znajdź lub utwórz pracownika
          if (!forceNewEmployee) {
            const fromEmps = emps?.find(e =>
              `${e.first_name} ${e.last_name}`.trim() === resolvedName || e.first_name === resolvedName
            )
            if (fromEmps) employeeId = fromEmps.id
          }
          
          if (!employeeId) {
            // Utwórz nowego pracownika
            const parts = resolvedName.split(' ')
            const firstName = parts[0] || 'Pracownik'
            const lastName = parts.slice(1).join(' ') || ''
            const createRes = await pwaApi.createEmployee(firstName, lastName)
            employeeId = createRes.employee.id
            setApiEmployees(p => p ? [...p, createRes.employee] : [createRes.employee])
          }
          
          // Zapisz odrzuconą notatkę (oryginalny tekst, nie cleanText)
          if (employeeId) {
            await pwaApi.saveRejectedConversation(
              employeeId,
              fullText,  // ORYGINALNY tekst, niepoprawiony przez LLM
              analyzeResult.status,
              analyzeResult.reason,
              analyzeResult.warningText
            )
            console.log('[DEBUG] Rejected conversation saved to DB')
          }
        } catch (saveErr) {
          console.error('[DEBUG] Failed to save rejected conversation:', saveErr)
          // Nie przerywamy - onGuardBlock i tak ma być wywołany
        }
        
        // NIE wywołujemy onDone — onDone = navigateTo(2), nie chcemy nawigować
        // Zamiast tego: onGuardBlock (np. resetSession po 2s) lub brak akcji
        onGuardBlock?.()
        return
      }

      const resolvedName = (analyzeResult.detectedName && analyzeResult.detectedName !== 'PracownikX')
        ? analyzeResult.detectedName
        : employeeName
      const cleanText = analyzeResult.cleanText || fullText

      // Szukamy pracownika TYLKO w apiEmployees (lista pracowników)
      // NIGDY w conversationItems - każda rozmowa to osobny wpis, zawsze tworzymy NOWĄ rozmowę
      // Gdy forceNewEmployee=true (heurystyka), zawsze tworzymy nowego pracownika
      let found: { id: number } | null = null
      if (!forceNewEmployee) {
        const fromEmps = emps?.find(e =>
          `${e.first_name} ${e.last_name}`.trim() === resolvedName || e.first_name === resolvedName
        )
        found = fromEmps ? { id: fromEmps.id } : null
      }

      if (found) {
        console.log('[DEBUG] Found existing employee:', found.id)
        launchStream(found.id, resolvedName, cleanText)
      } else {
        console.log('[DEBUG] Creating NEW employee:', resolvedName)
        const parts     = resolvedName.split(' ')
        const firstName = parts[0] || 'Pracownik'
        const lastName  = parts.slice(1).join(' ') || ''
        pwaApi.createEmployee(firstName, lastName)
          .then(res => {
            console.log('[DEBUG] createEmployee OK, id:', res.employee.id)
            setApiEmployees(p => p ? [...p, res.employee] : [res.employee])
            launchStream(res.employee.id, resolvedName, cleanText)
          })
          .catch(err => { console.error('[pipeline] createEmployee error:', err); onDone() })
      }
    })
  }, [startScriptStream])

  const finalizeAndSend = useCallback(({
    actionType,
    arrangements,
    arrangementList,
    existingNote,
    optimisticId,
    onClipboard,
    onDone,
  }: {
    actionType: 'NOTE' | 'WARNING' | 'REPRIMAND'
    arrangements?: string
    arrangementList?: Arrangement[]
    existingNote?: string
    optimisticId?: string
    onClipboard?: (text: string) => void
    onDone?: () => void
  }) => {
    console.log('[finalizeAndSend] ===== START =====')
    console.log('[finalizeAndSend] actionType:', actionType)
    console.log('[finalizeAndSend] arrangementList received:', arrangementList?.length, JSON.stringify(arrangementList))
    console.log('[finalizeAndSend] conversationId:', conversationId)
    console.log('[finalizeAndSend] optimisticId:', optimisticId)
    
    // FIX: Nie wywołuj API dla tymczasowych ID (tmp_*) - jeszcze nie ma ich w bazie
    const isTempId = typeof conversationId === 'string' && conversationId.startsWith('tmp_')
    if (isTempId) {
      console.log('[finalizeAndSend] Skipping API calls for temp conversationId:', conversationId)
      if (decisionContent && onClipboard) onClipboard(decisionContent)
      onDone?.()
      return
    }
    
    if (!conversationId) {
      console.log('[finalizeAndSend] No conversationId, skipping')
      if (decisionContent && onClipboard) onClipboard(decisionContent)
      onDone?.()
      return
    }

    const arrangementsToSave = arrangements?.trim() || arrangementsText.trim()

    console.log('[finalizeAndSend] Calling pwaApi.finalizeConversation with arrangementList:', arrangementList?.length)
    const finalize = pwaApi.finalizeConversation(conversationId, arrangementsToSave || ' ', undefined, existingNote, arrangementList)
      .then(res => { 
        console.log('[finalizeAndSend] finalizeConversation SUCCESS:', res.fullNote?.substring(0, 50))
        setDecisionContent(res.fullNote); 
        return res.fullNote 
      })
      .catch(err => {
        console.error('[finalizeAndSend] finalizeConversation ERROR:', err)
        throw err // Przekazuj błąd dalej
      })

    finalize
      .then(note => {
        console.log('[finalizeAndSend] note received, calling executeAction')
        if (note && onClipboard) onClipboard(note)
        return pwaApi.executeAction(conversationId, actionType)
          .then(res => {
            console.log('[finalizeAndSend] executeAction SUCCESS')
            if (res?.content && onClipboard) onClipboard(res.content)
            else if (res?.plainText && onClipboard) onClipboard(res.plainText)
          })
          .catch(err => {
            console.error('[finalizeAndSend] executeAction ERROR:', err)
          })
      })
      .catch(err => {
        console.error('[finalizeAndSend] MAIN ERROR:', err)
        // Jeśli błąd 429 (Too Many Requests), nadal aktualizuj optimistic item
        if (err.message && err.message.includes('Too Many Requests')) {
          console.log('[finalizeAndSend] 429 error - will still update optimistic item')
        }
      })
      .finally(async () => {
        // Jeśli mamy optimisticId, zaktualizuj tylko optimistic item zamiast odświeżać całą listę
        if (optimisticId && conversationId) {
          try {
            console.log('[finalizeAndSend] Updating optimistic item:', optimisticId)
            const backendData = await pwaApi.getConversation(conversationId)
            
            setConversationItems(prev => {
              const existingOptimistic = (prev ?? []).find((item: any) => item.optimisticId === optimisticId)
              // Użyj arrangements_json z backendu (finalizeConversation już je zapisał),
              // fallback na optimistic insert jeśli backend zwrócił puste
              const backendArr = backendData.arrangements_json
              const optimisticArr = existingOptimistic?.arrangements_json
              const finalArrangements = (Array.isArray(backendArr) && backendArr.length > 0)
                ? backendArr
                : optimisticArr || null

              const fullConversation = {
                conversation_id: backendData.conversationId,
                employee_id: 0,
                first_name: backendData.employeeFirstName || '',
                last_name: backendData.employeeLastName || '',
                position: null,
                short_summary: backendData.shortSummary || null,
                long_summary: backendData.fullNote || backendData.shortSummary || null,
                arrangements_transcription: null,
                arrangements_json: finalArrangements,
                conversation_status: undefined,
                created_at: backendData.createdAt || new Date().toISOString(),
                is_indexing: false,
                _optimistic: false,
              }

              if (existingOptimistic) {
                // Optimistic item znaleziony — zamień go
                return (prev ?? []).map(item => 
                  item.optimisticId === optimisticId 
                    ? { ...fullConversation, optimisticId: undefined }
                    : item
                )
              } else {
                // Fallback: optimistic item zniknął (np. polling go zastąpił) —
                // znajdź item po conversation_id i zaktualizuj go pełnymi danymi
                console.log('[finalizeAndSend] Optimistic item not found, updating by conversation_id')
                const convIdNum = backendData.conversationId
                const exists = (prev ?? []).some(item => item.conversation_id === convIdNum)
                if (exists) {
                  return (prev ?? []).map(item =>
                    item.conversation_id === convIdNum
                      ? { ...fullConversation, optimisticId: undefined }
                      : item
                  )
                } else {
                  // Item w ogóle nie istnieje w stanie — wstaw na początek
                  return [{ ...fullConversation, optimisticId: undefined }, ...(prev ?? [])]
                }
              }
            })
            loadEmployees()
          } catch (err) {
            console.error('[finalizeAndSend] Failed to update optimistic item:', err)
            // Fallback: odśwież całą listę
            try {
              const r = await pwaApi.getConversations()
              setConversationItems(r.items)
              loadEmployees()
            } catch {}
          }
        } else {
          // Fallback: odśwież całą listę (backward compatibility)
          try {
            const r = await pwaApi.getConversations()
            setConversationItems(r.items)
            loadEmployees()
          } catch {}
        }
        onDone?.()
      })
  }, [conversationId, decisionContent, arrangementsText])

  const resetConversation = useCallback(() => {
    abortScriptStream()
    setConversationId(null)
    setSelectedEmployee('')
    setTailoredScript(null)
    setScriptLoading(false)
    setScriptBriefing(null)
    setScriptModel(null)  // E2: reset modelu
    setScriptSteps([])
    setScriptStreamDone(false)
    setArrangementsText('')
    setDecisionContent('')
    setTranscriptionText('')
    setNoteUpdate(null)
    setIsProcessing(false)
    setGuardStatus(null)
    setComplianceWarning(null)
    setIsIncident(false)
    setIncidentFormState(null)
    setOriginalIncidentForm(null)
    setLastNoteId(null)
  }, [abortScriptStream, setLastNoteId])

  const resetConversationId = useCallback(() => {
    setConversationId(null)
  }, [])

  useEffect(() => {
    loadBaseDictionaries().catch(() => {})
  }, [])

  return {
    conversationId, selectedEmployee, tailoredScript, scriptLoading,
    scriptBriefing, scriptModel, scriptSteps, scriptStreamDone,  // E2: dodano scriptModel
    arrangementsText, decisionContent, apiEmployees,
    conversationItems, isProcessing, transcriptionText, noteUpdate,
    guardStatus, complianceWarning, isIncident, incidentForm, originalIncidentForm, lastNoteId,
    setSelectedEmployee, setArrangementsText, setDecisionContent,
    setTranscriptionText, setNoteUpdate, setIsIncident,
    setGuardStatus, setComplianceWarning, setScriptBriefing, setLastNoteId,
    setOriginalIncidentForm,
    loadEmployees, setIncidentForm: setIncidentFormState,
    loadConversationItems, setApiEmployees, setConversationItems,
    abortScriptStream, runDictationPipeline, finalizeAndSend,
    resetConversation, resetConversationId,
  }
}
